import type {
  ApiEffect,
  EpgInfoDto,
  TimeRange,
  TvChannel,
  TvEpgApiDto,
} from '@setplex/tria-api'
import type { PlayerAllTvQueries } from '~/shared/interfaces'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import {
  generateEmptyProgramForNow,
  getCurrentEpgPeriod,
  getCurrentProgram,
} from '~/entities/player/tv/lib'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'

export const initPlayerPageTvPlaying = createEvent<PlayerAllTvQueries>()

sample({
  clock: initPlayerPageTvPlaying,
  fn: ({ categoryId, ...initPlayerPageTvParams }) => ({
    ...initPlayerPageTvParams,
    categoryId: categoryId || 0,
  }),
  target: api.tv.generateTvPlaylistAbortableFx,
})

export const setCurrentChannel = createEvent<TvChannel>()

// update epg data (f.e. when previous is not actual anymore)
export const updatePlayingChannelEPGInfo = createEvent()
export const $currentChannel = restore(setCurrentChannel, null)

export const $currentChannelSSE = combine(
  $currentChannel,
  ssePurchasesModel.$channel,
  (channel, adjust) => (channel ? adjust(channel) : channel)
)

export const getUrlAndEpgWithRewind = createEvent<{ period: TimeRange }>()

// gets epg and live url after
export const getEpgAndLiveUrl = createEvent()

// gets 1.rewound url (epg not requested coz no rewind deeper than cur.program)
export const getRewoundUrl = createEvent<{
  rewindType?: string
  urlStartTime?: number
  targetStartTime?: number
}>()

export const getEpgInfoForTvChannelFx: ApiEffect<
  { period: TimeRange },
  { epg: TvEpgApiDto; period: TimeRange }
> = attach({
  effect: api.tv.getEpgInformationAbortableFx,
  source: { channel: $currentChannelSSE },
  mapParams: ({ period }, { channel }) => ({
    period,
    epgIds: [channel?.epgId || ''],
  }),
})

sample({
  clock: getEpgAndLiveUrl,
  fn: () => ({ period: getCurrentEpgPeriod() }),
  target: getEpgInfoForTvChannelFx,
})

export const updateEpgInfoForTvChannelFx: ApiEffect<
  { period: TimeRange },
  { epg: TvEpgApiDto; period: TimeRange }
> = attach({
  effect: api.tv.getEpgInformationAbortableFx,
  source: { channel: $currentChannelSSE },
  mapParams: ({ period }, { channel }) => ({
    period,
    epgIds: [channel?.epgId || ''],
  }),
})

// real program
export const $program = createStore<EpgInfoDto | null>(null)
  .on(getEpgInfoForTvChannelFx.doneData, (_, payload) =>
    getCurrentProgram(payload)
  )
  .on(getEpgInfoForTvChannelFx.fail, () => generateEmptyProgramForNow())
  .on(updateEpgInfoForTvChannelFx.doneData, (_, payload) =>
    getCurrentProgram(payload)
  )
  .on(updateEpgInfoForTvChannelFx.fail, () => generateEmptyProgramForNow())
  .reset([
    $currentChannelSSE,
    getEpgInfoForTvChannelFx,
    updateEpgInfoForTvChannelFx,
  ])

sample({
  clock: updatePlayingChannelEPGInfo,
  source: $currentChannelSSE,
  filter: (currentChannel) =>
    currentChannel != null && currentChannel.epgId != null,
  fn: () => ({ period: getCurrentEpgPeriod() }),
  target: updateEpgInfoForTvChannelFx,
})
